import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { calculateCartGST } from '../utils/gstUtils';

const Orders = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const placedOrderId = location.state?.orderId;
  const [showPlacedBanner, setShowPlacedBanner] = useState(!!placedOrderId);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
      
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchOrders();
    }
  }, [currentUser, fetchOrders]);

  const getStatusColor = (status) => {
    // Match theme colors used across the site and admin
    const s = status || 'pending';
    if (s === 'pending') return 'bg-gold text-deep-brown';
    if (s === 'processing') return 'bg-deep-brown text-cream';
    if (s === 'shipped') return 'bg-warm-gray text-white';
    if (s === 'delivered') return 'bg-sage text-white';
    if (s === 'cancelled') return 'bg-red-600 text-white';
    return 'bg-warm-gray/20 text-deep-brown';
  };

  // Highlight CSS for a recently placed order
  const highlightClass = 'ring-2 ring-gold';

  // When orders load and we have a placedOrderId, scroll it into view and auto-hide banner
  useEffect(() => {
    if (!loading && placedOrderId) {
      const el = document.getElementById(`order-card-${placedOrderId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const t = setTimeout(() => setShowPlacedBanner(false), 6000);
      return () => clearTimeout(t);
    }
  }, [loading, placedOrderId]);

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <div className="text-center py-20">
              <h1 className="text-4xl font-serif text-deep-brown mb-4">Please Login</h1>
              <p className="text-warm-gray mb-8">You need to be logged in to view your orders.</p>
              <Link
                to="/login"
                className="bg-deep-brown text-cream px-8 py-3 rounded-md hover:bg-warm-gray transition duration-300 font-medium"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-cream pt-20 pb-16">
        <div className="section-padding">
          <div className="container-max">
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-deep-brown"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-serif text-deep-brown mb-8">My Orders</h1>
            {showPlacedBanner && placedOrderId && (
              <div className="mb-6 rounded-md border border-warm-gray/20 bg-cream/60 p-4 flex items-start gap-3">
                <div className="mt-0.5">
                  <svg className="h-5 w-5 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-deep-brown font-medium">Order placed successfully!</p>
                  <p className="text-sm text-warm-gray">Your order ID is <span className="font-mono text-deep-brown">#{placedOrderId.substring(0,8).toUpperCase()}</span>.</p>
                </div>
              </div>
            )}
            
            {orders.length === 0 ? (
              <div className="text-center py-20">
                <div className="mb-8">
                  <svg className="mx-auto h-24 w-24 text-warm-gray" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l-1 12H6L5 9z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-serif text-deep-brown mb-4">No Orders Yet</h2>
                <p className="text-warm-gray mb-8">You haven't placed any orders yet.</p>
                <Link
                  to="/"
                  className="bg-deep-brown text-cream px-8 py-3 rounded-md hover:bg-warm-gray transition duration-300 font-medium"
                >
                  Start Shopping
                </Link>
              </div>
            ) : (
              <div className="space-y-6">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    id={`order-card-${order.id}`}
                    className={`bg-white rounded-lg shadow-sm border border-warm-gray/20 overflow-hidden ${placedOrderId === order.id ? highlightClass : ''}`}
                  >
                    {/* Order Header */}
                    <div className="bg-cream/50 px-6 py-4 border-b border-warm-gray/20">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6">
                          <div>
                            <p className="text-sm text-warm-gray">Order ID</p>
                            <p className="font-medium text-deep-brown">#{order.id.substring(0, 8).toUpperCase()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-warm-gray">Date</p>
                            <p className="font-medium text-deep-brown">
                              {order.createdAt?.toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-warm-gray">Total</p>
                            <p className="font-medium text-deep-brown">₹{order.total?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-warm-gray">Payment</p>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-deep-brown capitalize">{order.paymentMethod || '—'}</p>
                              {order.paymentStatus && (
                                <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                                  order.paymentStatus === 'paid'
                                    ? 'bg-sage text-white'
                                    : order.paymentStatus === 'pending'
                                    ? 'bg-gold text-deep-brown'
                                    : order.paymentStatus === 'failed'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-warm-gray/20 text-deep-brown'
                                }`}>
                                  {order.paymentStatus}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 sm:mt-0">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status || 'pending')}`}>
                            {(order.status || 'pending').charAt(0).toUpperCase() + (order.status || 'pending').slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="px-6 py-4">
                      <div className="space-y-4">
                        {order.items?.map((item, index) => (
                          <div key={index} className="flex items-center space-x-4">
                            <img
                              src={item.images?.[0] || '/placeholder-image.jpg'}
                              alt={item.title}
                              className="h-16 w-16 object-cover rounded-md"
                            />
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-serif font-medium text-deep-brown truncate">
                                {item.title}
                              </h3>
                              <p className="text-sm text-warm-gray">{item.brand}</p>
                              {item.selectedSize && (
                                <p className="text-sm text-warm-gray">Size: {item.selectedSize}</p>
                              )}
                              <p className="text-sm text-warm-gray">Quantity: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-deep-brown">
                                ₹{(item.price * item.quantity).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Order Summary */}
                      <div className="mt-6 pt-4 border-t border-warm-gray/20">
                        <div className="flex justify-between items-center">
                          <div className="text-sm text-warm-gray">
                            <p>Items: ₹{order.subtotal?.toLocaleString()}</p>
                            {order.discount > 0 && (
                              <p className="text-green-600">
                                Discount: -₹{order.discount?.toLocaleString()}
                                {order.appliedCoupon && ` (${order.appliedCoupon.code})`}
                              </p>
                            )}
                            <p>Shipping: {order.shipping && order.shipping > 0 ? `₹${order.shipping.toLocaleString()}` : 'Free'}</p>
                            <p className="text-xs">GST Included: ₹{calculateCartGST(order.total || 0).toFixed(2)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-deep-brown">
                              Total: ₹{order.total?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Shipping Address */}
                      {order.shippingAddress && (
                        <div className="mt-6 pt-4 border-t border-warm-gray/20">
                          <h4 className="text-sm font-medium text-deep-brown mb-2">Shipping Address</h4>
                          <div className="text-sm text-warm-gray">
                            <p>{order.shippingAddress.name}</p>
                            <p>{order.shippingAddress.address}</p>
                            <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</p>
                            <p>{order.shippingAddress.phone}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Orders;